import React, { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { reducedMotion } from '../../shared/Motion';
import { METRIC_STYLE, fmtVal } from './PerfUi';

// Presentation only — all numbers come from GET /crm/admin/performance rows (actual / target / pct).
const styleFor = (key) => METRIC_STYLE[key] || METRIC_STYLE.new_leads;
export const pctOf = (actual, target) => (target > 0 ? Math.round(Math.min(actual / target, 9.99) * 100) : null);
export const pctTone = (pct) => pct == null ? 'text-slate-400' : pct >= 100 ? 'text-emerald-600' : pct >= 60 ? 'text-slate-600' : 'text-amber-600';

const useGrow = (pct) => {
  const [w, setW] = useState(reducedMotion() ? pct : 0);
  useEffect(() => { const id = requestAnimationFrame(() => setW(pct)); return () => cancelAnimationFrame(id); }, [pct]);
  return w;
};

export const Bar = ({ pct, color, className = 'h-1.5 w-full' }) => {
  const fill = useGrow(pct == null ? 0 : Math.min(pct, 100));
  return (
    <div className={`${className} rounded-full overflow-hidden`} style={{ background: `${color}22` }}>
      <div className="pf-bar h-full rounded-full" style={{ width: `${fill}%`, background: pct == null ? '#cbd5e1' : color }} />
    </div>
  );
};

export const Avatar = ({ name, size = 'h-9 w-9 text-[12px]' }) => {
  const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('');
  return <span className={`${size} shrink-0 rounded-full text-white font-bold flex items-center justify-center`} style={{ background: 'linear-gradient(135deg,#2563eb,#6d4ee0)' }} data-testid="sp-avatar">{initials}</span>;
};

/** Team totals (sum of actuals / sum of targets) or a single person's metric. */
export const TeamKpiCard = ({ m, delay = 0, prefix = 'Total ' }) => {
  const st = styleFor(m.key); const Icon = st.icon; const pct = pctOf(m.actual, m.target);
  return (
    <div className={`lv-card lv-lift an-rise p-3.5 bg-gradient-to-br from-white ${st.tint}`} style={{ animationDelay: `${delay}ms` }} data-testid={`sp-kpi-${m.key}`}>
      <div className="flex items-center gap-2.5">
        <span className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center ${st.tile}`}><Icon className="h-4 w-4" /></span>
        <p className="text-[12.5px] font-semibold text-slate-600 truncate">{prefix}{m.label}</p>
      </div>
      <p className="mt-2 tabular-nums leading-none">
        <span className="text-[24px] font-extrabold" style={{ color: st.color }} data-testid={`sp-kpi-${m.key}-actual`}>{fmtVal(m.actual, m.kind)}</span>
        <span className="text-[14px] text-slate-400 font-medium"> / </span>
        <span className="text-[14px] text-slate-600 font-semibold" data-testid={`sp-kpi-${m.key}-target`}>{m.target > 0 ? fmtVal(m.target, m.kind) : '—'}</span>
      </p>
      <div className="mt-2.5 flex items-center gap-2">
        <Bar pct={pct} color={st.color} />
        <span className={`text-[11.5px] font-bold tabular-nums w-9 text-right ${pctTone(pct)}`} data-testid={`sp-kpi-${m.key}-pct`}>{pct == null ? '—' : `${pct}%`}</span>
      </div>
      {m.target <= 0 && <p className="text-[10.5px] text-slate-400 mt-1" data-testid={`sp-kpi-${m.key}-notarget`}>No target set</p>}
    </div>
  );
};

/** Sum a metric across rows → { key,label,kind, actual, target }. */
export const teamTotals = (rows, defs) => defs.map(d => rows.reduce((acc, r) => {
  const m = r.metrics.find(x => x.key === d.key) || { actual: 0, target: 0 };
  return { ...acc, actual: acc.actual + (m.actual || 0), target: acc.target + (m.target || 0) };
}, { key: d.key, label: d.label, kind: d.kind, actual: 0, target: 0 }));

export const MetricTabs = ({ defs, value, onChange }) => (
  <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5" role="tablist" data-testid="sp-metric-tabs">
    {[{ key: 'all', label: 'By Salesperson', icon: Users }, ...defs.map(d => ({ key: d.key, label: d.label, icon: styleFor(d.key).icon }))].map(t => {
      const active = value === t.key; const Icon = t.icon;
      return (
        <button key={t.key} type="button" role="tab" aria-selected={active} onClick={() => onChange(t.key)} data-testid={`sp-tab-${t.key}`}
          className={`an-btn an-tab shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl text-[12.5px] font-semibold whitespace-nowrap ${active ? 'an-btn-primary' : 'bg-white border border-slate-200 text-slate-600 hover:border-violet-200 hover:text-violet-700 hover:bg-violet-50/60'}`}>
          <Icon className="h-3.5 w-3.5" />{t.label}
        </button>
      );
    })}
  </div>
);

/** Compact "actual / target  pct + bar" cell used by the team table. */
export const MetricCell = ({ m, onActual, testid }) => {
  const st = styleFor(m.key);
  return (
    <div className="min-w-[96px]" data-testid={testid}>
      <div className="flex items-baseline gap-1 tabular-nums whitespace-nowrap">
        <button type="button" onClick={onActual} className="text-[13px] font-bold hover:underline decoration-dotted rounded px-0.5" style={{ color: st.color }} data-testid={`${testid}-actual`}>{fmtVal(m.actual, m.kind)}</button>
        <span className="text-[12px] text-slate-500 font-medium">/ {m.target > 0 ? fmtVal(m.target, m.kind) : '—'}</span>
        <span className={`ml-auto text-[11px] font-bold ${pctTone(m.pct)}`} data-testid={`${testid}-pct`}>{m.pct == null ? '—' : `${m.pct}%`}</span>
      </div>
      <Bar pct={m.pct} color={st.color} className="h-1.5 w-full mt-1" />
    </div>
  );
};

/** Ranked single-metric leaderboard (sorted by pct desc, then actual). */
export const rankRows = (rows, key) => rows.map(r => ({ r, m: r.metrics.find(x => x.key === key) })).filter(x => x.m)
  .sort((a, b) => ((b.m.pct ?? -1) - (a.m.pct ?? -1)) || (b.m.actual - a.m.actual));

/** `ranked` = full-dataset ranking (rankRows); `from`/`to` = the visible page slice (rank numbers stay absolute). */
export const Leaderboard = ({ ranked, def, from = 0, to, onPick, onActual }) => {
  const st = styleFor(def.key);
  if (!ranked.length) return <div className="px-5 py-10 text-center text-sm text-slate-400" data-testid="sp-leaderboard-empty">No Sales & Marketing users yet.</div>;
  return (
    <div className="divide-y divide-slate-100" data-testid="sp-leaderboard">
      {ranked.slice(from, to).map(({ r, m }, idx) => { const i = from + idx; return (
        <div key={r.user_id} className="an-row grid grid-cols-[32px_minmax(0,1fr)_minmax(0,2fr)_auto] items-center gap-4 px-5 py-3 hover:bg-violet-50/30" data-testid="sp-leader-row">
          <span className={`h-7 w-7 rounded-full flex items-center justify-center text-[11.5px] font-extrabold ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-700' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`} data-testid="sp-leader-rank">{i + 1}</span>
          <button type="button" onClick={() => onPick(r)} className="flex items-center gap-2.5 min-w-0 text-left" data-testid="sp-leader-name"><Avatar name={r.name} size="h-8 w-8 text-[11px]" /><span className="text-[13px] font-semibold text-slate-900 truncate hover:underline">{r.name}</span></button>
          <Bar pct={m.pct} color={st.color} className="h-2 w-full" />
          <div className="tabular-nums whitespace-nowrap text-right">
            <button type="button" onClick={() => onActual(r, m)} className="text-[13px] font-bold hover:underline decoration-dotted" style={{ color: st.color }} data-testid="sp-leader-actual">{fmtVal(m.actual, m.kind)}</button>
            <span className="text-[12px] text-slate-500"> / {m.target > 0 ? fmtVal(m.target, m.kind) : '—'}</span>
            <span className={`ml-2 inline-flex min-w-[44px] justify-end text-[12px] font-bold ${pctTone(m.pct)}`} data-testid="sp-leader-pct">{m.pct == null ? 'No target' : `${m.pct}%`}</span>
          </div>
        </div>
      ); })}
    </div>
  );
};

/** Individual view row — label · bar · actual/target · pct (actual clickable → drill-down). */
export const PersonRow = ({ m, onActual }) => {
  const st = styleFor(m.key); const Icon = st.icon;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[230px_minmax(0,1fr)_auto_auto] items-center gap-x-4 gap-y-1.5 py-2.5 border-t border-slate-100 first:border-t-0" data-testid={`perf-${m.key}`}>
      <div className="flex items-center gap-2.5 min-w-0"><span className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${st.tile}`}><Icon className="h-4 w-4" /></span><span className="text-[13px] font-medium text-slate-800">{m.label}</span></div>
      <div className="col-span-2 sm:col-span-1 order-3 sm:order-none"><Bar pct={m.pct} color={st.color} className="h-2 w-full" /></div>
      <div className="text-[13px] tabular-nums whitespace-nowrap text-right sm:min-w-[110px]">
        <button type="button" onClick={onActual} className="font-bold hover:underline decoration-dotted rounded px-0.5" style={{ color: st.color }} data-testid={`perf-actual-${m.key}`}>{fmtVal(m.actual, m.kind)}</button>
        <span className="text-slate-400"> / </span><span className="text-slate-500" data-testid={`perf-target-${m.key}`}>{m.target > 0 ? fmtVal(m.target, m.kind) : '—'}</span>
      </div>
      <div className="justify-self-end">
        {m.target > 0 ? <span className={`inline-flex min-w-[46px] justify-center rounded-md px-1.5 py-0.5 text-[11.5px] font-bold tabular-nums ${m.pct >= 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`} data-testid={`perf-pct-${m.key}`}>{m.pct}%</span>
          : <span className="inline-flex rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold bg-slate-100 text-slate-400 whitespace-nowrap" data-testid={`perf-notarget-${m.key}`}>No target set</span>}
      </div>
    </div>
  );
};
